import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  IconButton,
  Collapse,
  Chip,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  FormGroup,
  FormControlLabel,
  Checkbox,
  OutlinedInput,
  ListItemText
} from '@mui/material';
import {
  KeyboardArrowDown as KeyboardArrowDownIcon,
  KeyboardArrowUp as KeyboardArrowUpIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Add as AddIcon,
  RestartAlt as RestartAltIcon,
  Key as KeyIcon,
  Extension as ExtensionIcon,
  Apps as AppsIcon,
  ArrowDownward,
  ArrowUpward
} from '@mui/icons-material';
import { getAllUsers, resetUserData } from '../services/userService';
import { decodePassword } from '../utils/cryptoUtils';
import { useAuth } from '../contexts/AuthContext';
import { formatDateTime } from '../utils/dateUtils';
import { db } from '../services/firebase';
import { doc, updateDoc, collection, getDocs, setDoc } from 'firebase/firestore';
import { useTheme } from '@mui/material/styles';

const availableModules = [
  {
    id: 'calendar',
    title: 'Calendário'
  },
  {
    id: 'habits',
    title: 'Hábitos'
  },
  {
    id: 'goals',
    title: 'Metas'
  },
  {
    id: 'challenges',
    title: 'Desafios'
  },
  {
    id: 'productivity',
    title: 'Produtividade'
  },
  {
    id: 'notes',
    title: 'Notas'
  },
  {
    id: 'statistics',
    title: 'Estatísticas'
  }
];

function UserRow({ user, onDelete, onEditRole, onEditModules }) {
  const [open, setOpen] = useState(false);

  // Log para debug
  console.log('Dados do usuário:', {
    email: user.email,
    lastLogin: user.lastLogin,
    metadataLastLogin: user.metadata?.lastLogin,
    lastActivity: user.lastActivity,
    rawUser: user
  });

  return (
    <>
      <TableRow sx={{ '& > *': { borderBottom: 'unset' } }}>
        <TableCell>
          <IconButton
            aria-label="expand row"
            size="small"
            onClick={() => setOpen(!open)}
          >
            {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
          </IconButton>
        </TableCell>
        <TableCell component="th" scope="row">
          {user.email}
        </TableCell>
        <TableCell>
          {user.name || '-'}
        </TableCell>
        <TableCell align="center">
          {user.createdAt && user.createdAt.toDate ? formatDateTime(user.createdAt.toDate()) : (user.createdAt ? formatDateTime(user.createdAt) : '-')}
        </TableCell>
        <TableCell align="center">
          {formatDateTime(user.lastLogin || user.metadata?.lastLogin) || '-'}
        </TableCell>
        <TableCell align="center">
          {formatDateTime(user.lastActivity) || '-'}
        </TableCell>
        <TableCell align="center">
          <Chip 
            label={
              user.role === 'admin' ? 'Administrador' : 
              user.role === 'pro' ? 'Pro' : 'Usuário'
            } 
            color={
              user.role === 'admin' ? 'primary' : 
              user.role === 'pro' ? 'success' : 'default'
            }
          />
        </TableCell>
        <TableCell align="center">
          {user.role || 'Usuário'}
        </TableCell>
        <TableCell align="right">
          <IconButton
            color="warning"
            onClick={() => onDelete(user)}
            title="Excluir conta"
          >
            <DeleteIcon />
          </IconButton>
          <IconButton
            color="primary"
            onClick={() => onEditRole(user)}
            title="Editar tipo de usuário"
            sx={{ mr: 1 }}
          >
            <EditIcon />
          </IconButton>
          <IconButton
            color="info"
            onClick={() => onEditModules(user)}
            title="Configurar módulos"
            sx={{ mr: 1 }}
          >
            <AppsIcon />
          </IconButton>
        </TableCell>
      </TableRow>
      <TableRow>
        <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={6}>
          <Collapse in={open} timeout="auto" unmountOnExit>
            <Box sx={{ margin: 1 }}>
              <Typography variant="h6" gutterBottom component="div">
                Detalhes do Usuário
              </Typography>
              <Table size="small" aria-label="user details">
                <TableBody>
                  <TableRow>
                    <TableCell component="th" scope="row">Senha</TableCell>
                    <TableCell>{decodePassword(user.hashedPassword)}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell component="th" scope="row">Último Login</TableCell>
                    <TableCell>{formatDateTime(user.metadata?.lastLogin)}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell component="th" scope="row">Último Logout</TableCell>
                    <TableCell>{formatDateTime(user.metadata?.lastLogout)}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell component="th" scope="row">Última Alteração de Senha</TableCell>
                    <TableCell>{formatDateTime(user.passwordLastChanged)}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell component="th" scope="row">Último Reset de Dados</TableCell>
                    <TableCell>{formatDateTime(user.metadata?.lastReset)}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell component="th" scope="row">Quantidade de Logins</TableCell>
                    <TableCell>{user.metadata?.loginCount || 0}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell component="th" scope="row">Dispositivo</TableCell>
                    <TableCell>{user.metadata?.deviceInfo?.userAgent || 'Não disponível'}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell component="th" scope="row">Plataforma</TableCell>
                    <TableCell>{user.metadata?.deviceInfo?.platform || 'Não disponível'}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell component="th" scope="row">Idioma</TableCell>
                    <TableCell>{user.metadata?.deviceInfo?.language || 'Não disponível'}</TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
}

export default function UserManagement({ customColors }) {
  const { signup, changePassword, deleteUser } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [selectedUser, setSelectedUser] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [roleDialogOpen, setRoleDialogOpen] = useState(false);
  const [modulesDialogOpen, setModulesDialogOpen] = useState(false);
  const [selectedModules, setSelectedModules] = useState([]);
  const [selectedRole, setSelectedRole] = useState('user');
  
  // Estados para os diálogos
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [passwordDialogOpen, setPasswordDialogOpen] = useState(false);
  const [resetDialogOpen, setResetDialogOpen] = useState(false);
  const [newUserEmail, setNewUserEmail] = useState('');
  const [newUserPassword, setNewUserPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [sortConfig, setSortConfig] = useState({ key: 'email', direction: 'asc' });
  const theme = useTheme();
  const [roleFilter, setRoleFilter] = useState([]);

  useEffect(() => {
    loadUsers();
  }, []);

  const loadUsers = async () => {
    try {
      setLoading(true);
      setError('');
      const usersCollection = collection(db, 'users');
      const usersSnapshot = await getDocs(usersCollection);
      const usersData = usersSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        enabledModules: doc.data().enabledModules || availableModules.map(m => m.id)
      }));
      setUsers(usersData);
    } catch (error) {
      console.error('Erro ao carregar usuários:', error);
      setError('Erro ao carregar usuários. Por favor, tente novamente.');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateUser = async () => {
    if (!newUserEmail || !newUserPassword) {
      setError('Por favor, preencha todos os campos.');
      return;
    }

    try {
      setError('');
      // Cria o usuário no Firebase Auth
      const userCredential = await signup(newUserEmail, newUserPassword);
      
      // Cria o documento do usuário no Firestore
      const userRef = doc(db, 'users', userCredential.user.uid);
      await setDoc(userRef, {
        email: newUserEmail,
        role: 'user',
        enabledModules: availableModules.map(m => m.id), // Habilita todos os módulos por padrão
        createdAt: new Date().toISOString()
      });

      setSuccess('Usuário criado com sucesso!');
      setCreateDialogOpen(false);
      setNewUserEmail('');
      setNewUserPassword('');
      loadUsers(); // Recarrega a lista de usuários
    } catch (error) {
      console.error('Erro ao criar usuário:', error);
      setError('Erro ao criar usuário: ' + error.message);
    }
  };

  const handleDeleteUser = async () => {
    try {
      setError('');
      await deleteUser(selectedUser.id);
      setSuccess('Usuário excluído com sucesso!');
      setDeleteDialogOpen(false);
      loadUsers();
    } catch (error) {
      console.error('Erro ao excluir usuário:', error);
      setError('Erro ao excluir usuário: ' + error.message);
    }
  };

  const handleChangePassword = async () => {
    try {
      setError('');
      await changePassword(selectedUser.id, newPassword);
      setSuccess('Senha alterada com sucesso!');
      setPasswordDialogOpen(false);
      loadUsers();
      
      // Limpa o campo
      setNewPassword('');
    } catch (error) {
      console.error('Erro ao alterar senha:', error);
      setError('Erro ao alterar senha: ' + error.message);
    }
  };

  const handleResetUser = async () => {
    try {
      setError('');
      await resetUserData(selectedUser.id);
      setSuccess('Dados do usuário resetados com sucesso!');
      setResetDialogOpen(false);
      loadUsers();
    } catch (error) {
      console.error('Erro ao resetar dados do usuário:', error);
      setError('Erro ao resetar dados do usuário: ' + error.message);
    }
  };

  const handleEditRole = async () => {
    try {
      setError('');
      // Atualizar o role do usuário no Firestore
      const userRef = doc(db, 'users', selectedUser.id);
      await updateDoc(userRef, {
        role: selectedRole
      });
      setSuccess('Tipo de usuário alterado com sucesso!');
      setRoleDialogOpen(false);
      loadUsers();
    } catch (error) {
      console.error('Erro ao alterar tipo de usuário:', error);
      setError('Erro ao alterar tipo de usuário: ' + error.message);
    }
  };

  const handleEditModules = (user) => {
    setSelectedUser(user);
    setSelectedModules(user.enabledModules || availableModules.map(m => m.id));
    setModulesDialogOpen(true);
  };

  const handleSaveModules = async (userId, selectedModules) => {
    try {
      const userRef = doc(db, 'users', userId);
      await updateDoc(userRef, {
        enabledModules: selectedModules
      });
      setModulesDialogOpen(false);
      // Atualiza a lista de usuários para refletir as mudanças
      loadUsers();
    } catch (error) {
      console.error('Erro ao salvar módulos:', error);
      setError('Erro ao salvar módulos. Por favor, tente novamente.');
    }
  };

  const handleModuleToggle = (moduleId) => {
    setSelectedModules(prev => {
      if (prev.includes(moduleId)) {
        return prev.filter(id => id !== moduleId);
      } else {
        return [...prev, moduleId];
      }
    });
  };

  // Função para calcular o dashboard
  const getDashboard = () => {
    const porRole = {};
    users.forEach(user => {
      const role = user.role || 'sem role';
      porRole[role] = (porRole[role] || 0) + 1;
    });
    return {
      total: users.length,
      porRole
    };
  };

  const dashboard = getDashboard();

  // Função para ordenar os usuários
  const sortedUsers = React.useMemo(() => {
    const sorted = [...users];
    sorted.sort((a, b) => {
      let aValue = a[sortConfig.key];
      let bValue = b[sortConfig.key];

      // Para datas, converter para timestamp
      if (['createdAt', 'lastLogin', 'lastActivity'].includes(sortConfig.key)) {
        aValue = aValue && aValue.toDate ? aValue.toDate().getTime() : (aValue ? new Date(aValue).getTime() : null);
        bValue = bValue && bValue.toDate ? bValue.toDate().getTime() : (bValue ? new Date(bValue).getTime() : null);

        // Campos vazios vão para o final
        if (aValue === null && bValue === null) return 0;
        if (aValue === null) return 1;
        if (bValue === null) return -1;
      }

      // Para strings, comparar lowercase
      if (typeof aValue === 'string') aValue = aValue.toLowerCase();
      if (typeof bValue === 'string') bValue = bValue.toLowerCase();

      if (aValue < bValue) return sortConfig.direction === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortConfig.direction === 'asc' ? 1 : -1;
      return 0;
    });
    return sorted;
  }, [users, sortConfig]);

  // Função para alternar a ordenação
  const handleSort = (key) => {
    setSortConfig((prev) => {
      if (prev.key === key) {
        return { key, direction: prev.direction === 'asc' ? 'desc' : 'asc' };
      }
      return { key, direction: 'asc' };
    });
  };

  // Extrair todos os roles únicos dos usuários
  const allRoles = React.useMemo(() => {
    const roles = users.map(u => u.role || 'sem role');
    return Array.from(new Set(roles));
  }, [users]);

  // Filtrar usuários pelo(s) role(s) selecionado(s)
  const filteredUsers = React.useMemo(() => {
    if (!roleFilter.length) return sortedUsers;
    return sortedUsers.filter(u => roleFilter.includes(u.role || 'sem role'));
  }, [sortedUsers, roleFilter]);

  return (
    <Box sx={{ width: '100%', mt: 3 }}>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}
      
      <Box sx={{ mb: 2, display: 'flex', gap: 2 }}>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setCreateDialogOpen(true)}
        >
          Criar Novo Usuário
        </Button>
        <Button
          variant="outlined"
          onClick={loadUsers}
          disabled={loading}
        >
          Atualizar
        </Button>
      </Box>

      {/* FILTRO POR ROLE */}
      <Box sx={{ mb: 2, width: 300 }}>
        <FormControl fullWidth size="small">
          <InputLabel id="role-filter-label">Filtrar por Role</InputLabel>
          <Select
            labelId="role-filter-label"
            multiple
            value={roleFilter}
            onChange={e => setRoleFilter(typeof e.target.value === 'string' ? e.target.value.split(',') : e.target.value)}
            input={<OutlinedInput label="Filtrar por Role" />}
            renderValue={selected => selected.join(', ')}
          >
            {allRoles.map(role => (
              <MenuItem key={role} value={role}>
                <Checkbox checked={roleFilter.indexOf(role) > -1} />
                <ListItemText primary={role} />
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      {/* DASHBOARD */}
      <Paper sx={{ width: '100%', mb: 2, p: 2, background: theme.palette.mode === 'dark' ? theme.palette.background.paper : '#f5f5f5' }}>
        <Typography variant="h6" gutterBottom>Dashboard de Usuários</Typography>
        <Typography variant="body1"><strong>Total de usuários:</strong> {dashboard.total}</Typography>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mt: 1 }}>
          {Object.entries(dashboard.porRole).map(([role, count]) => (
            <Chip key={role} label={`${role}: ${count}`} color={role === 'admin' ? 'primary' : role === 'pro' ? 'success' : 'default'} />
          ))}
        </Box>
      </Paper>

      <Paper sx={{ width: '100%', mb: 2 }}>
        <TableContainer>
          <Table aria-label="collapsible table">
            <TableHead>
              <TableRow>
                <TableCell />
                <TableCell onClick={() => handleSort('email')} style={{ cursor: 'pointer' }}>
                  Email {sortConfig.key === 'email' && (sortConfig.direction === 'asc' ? <ArrowUpward fontSize="small" /> : <ArrowDownward fontSize="small" />)}
                </TableCell>
                <TableCell onClick={() => handleSort('name')} style={{ cursor: 'pointer' }}>
                  Nome {sortConfig.key === 'name' && (sortConfig.direction === 'asc' ? <ArrowUpward fontSize="small" /> : <ArrowDownward fontSize="small" />)}
                </TableCell>
                <TableCell align="center" onClick={() => handleSort('createdAt')} style={{ cursor: 'pointer' }}>
                  Criado em: {sortConfig.key === 'createdAt' && (sortConfig.direction === 'asc' ? <ArrowUpward fontSize="small" /> : <ArrowDownward fontSize="small" />)}
                </TableCell>
                <TableCell align="center" onClick={() => handleSort('lastLogin')} style={{ cursor: 'pointer' }}>
                  Último Login {sortConfig.key === 'lastLogin' && (sortConfig.direction === 'asc' ? <ArrowUpward fontSize="small" /> : <ArrowDownward fontSize="small" />)}
                </TableCell>
                <TableCell align="center" onClick={() => handleSort('lastActivity')} style={{ cursor: 'pointer' }}>
                  Última Atividade {sortConfig.key === 'lastActivity' && (sortConfig.direction === 'asc' ? <ArrowUpward fontSize="small" /> : <ArrowDownward fontSize="small" />)}
                </TableCell>
                <TableCell align="center" onClick={() => handleSort('role')} style={{ cursor: 'pointer' }}>
                  Role {sortConfig.key === 'role' && (sortConfig.direction === 'asc' ? <ArrowUpward fontSize="small" /> : <ArrowDownward fontSize="small" />)}
                </TableCell>
                <TableCell align="center">Tipo</TableCell>
                <TableCell align="right">Ações</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredUsers.map((user) => (
                <UserRow 
                  key={user.id} 
                  user={user}
                  onDelete={() => {
                    setSelectedUser(user);
                    setDeleteDialogOpen(true);
                  }}
                  onEditRole={() => {
                    setSelectedUser(user);
                    setSelectedRole(user.role || 'user');
                    setRoleDialogOpen(true);
                  }}
                  onEditModules={handleEditModules}
                />
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Diálogo de Criação de Usuário */}
      <Dialog open={createDialogOpen} onClose={() => setCreateDialogOpen(false)}>
        <DialogTitle>Criar Novo Usuário</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Email"
            type="email"
            fullWidth
            value={newUserEmail}
            onChange={(e) => setNewUserEmail(e.target.value)}
          />
          <TextField
            margin="dense"
            label="Senha"
            type="password"
            fullWidth
            value={newUserPassword}
            onChange={(e) => setNewUserPassword(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateDialogOpen(false)}>Cancelar</Button>
          <Button onClick={handleCreateUser} variant="contained">
            Criar
          </Button>
        </DialogActions>
      </Dialog>

      {/* Diálogo de Exclusão de Usuário */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Confirmar Exclusão</DialogTitle>
        <DialogContent>
          <Typography>
            Tem certeza que deseja excluir o usuário {selectedUser?.email}?
            Esta ação não pode ser desfeita.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancelar</Button>
          <Button onClick={handleDeleteUser} color="error" variant="contained">
            Excluir
          </Button>
        </DialogActions>
      </Dialog>

      {/* Diálogo de Alteração de Senha */}
      <Dialog open={passwordDialogOpen} onClose={() => setPasswordDialogOpen(false)}>
        <DialogTitle>Alterar Senha</DialogTitle>
        <DialogContent>
          <Typography gutterBottom color="warning.main" sx={{ mb: 2 }}>
            Nota: Por questões de segurança, a senha será atualizada apenas no registro do usuário.
            O usuário continuará usando sua senha atual para login.
            Para alterar a senha de login, o usuário deve fazer isso em sua própria conta.
          </Typography>
          <Typography gutterBottom>
            Alterando senha do usuário {selectedUser?.email}
          </Typography>
          <TextField
            autoFocus
            margin="dense"
            label="Nova Senha"
            type="password"
            fullWidth
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPasswordDialogOpen(false)}>Cancelar</Button>
          <Button onClick={handleChangePassword} variant="contained">
            Alterar
          </Button>
        </DialogActions>
      </Dialog>

      {/* Diálogo de Reset de Dados */}
      <Dialog open={resetDialogOpen} onClose={() => setResetDialogOpen(false)}>
        <DialogTitle>Confirmar Reset de Dados</DialogTitle>
        <DialogContent>
          <Typography color="error" paragraph>
            ATENÇÃO: Esta ação irá:
          </Typography>
          <ul>
            <li>Apagar todos os hábitos do usuário</li>
            <li>Resetar todas as configurações para o padrão</li>
            <li>Limpar todo o histórico de atividades</li>
          </ul>
          <Typography paragraph>
            Tem certeza que deseja resetar todos os dados do usuário {selectedUser?.email}?
          </Typography>
          <Typography color="error">
            Esta ação não pode ser desfeita.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setResetDialogOpen(false)}>Cancelar</Button>
          <Button onClick={handleResetUser} color="warning" variant="contained">
            Resetar Dados
          </Button>
        </DialogActions>
      </Dialog>

      {/* Diálogo de Edição de Role */}
      <Dialog open={roleDialogOpen} onClose={() => setRoleDialogOpen(false)}>
        <DialogTitle>Alterar Tipo de Usuário</DialogTitle>
        <DialogContent>
          <Typography gutterBottom sx={{ mb: 2 }}>
            Alterando tipo do usuário {selectedUser?.email}
          </Typography>
          <FormControl fullWidth>
            <InputLabel>Tipo de Usuário</InputLabel>
            <Select
              value={selectedRole}
              label="Tipo de Usuário"
              onChange={(e) => setSelectedRole(e.target.value)}
            >
              <MenuItem value="user">Usuário</MenuItem>
              <MenuItem value="pro">Pro</MenuItem>
              <MenuItem value="admin">Administrador</MenuItem>
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRoleDialogOpen(false)}>Cancelar</Button>
          <Button onClick={handleEditRole} variant="contained">
            Salvar
          </Button>
        </DialogActions>
      </Dialog>

      {/* Módulos Dialog */}
      <Dialog 
        open={modulesDialogOpen} 
        onClose={() => setModulesDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Configurar Módulos</DialogTitle>
        <DialogContent>
          <Typography variant="subtitle1" gutterBottom>
            Selecione os módulos que estarão disponíveis para {selectedUser?.email}
          </Typography>
          <FormGroup>
            {availableModules.map((module) => (
              <FormControlLabel
                key={module.id}
                control={
                  <Checkbox
                    checked={selectedModules.includes(module.id)}
                    onChange={() => handleModuleToggle(module.id)}
                  />
                }
                label={module.title}
              />
            ))}
          </FormGroup>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setModulesDialogOpen(false)}>Cancelar</Button>
          <Button 
            onClick={() => handleSaveModules(selectedUser.id, selectedModules)}
            variant="contained"
            color="primary"
          >
            Salvar
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
} 